<!-- eslint-disable max-len -->
<template>
  <b-modal
    v-model="showModal"
    title="Create New Budget"
    size="xl"
    centered
    hide-footer
    no-close-on-backdrop
    @hidden="resetWizard"
    @shown="onModalShown"
  >
    <div class="budget-wizard">
      <!-- Progress Indicator -->
      <div class="wizard-progress">
        <div class="progress-steps">
          <div
            v-for="stepNum in totalSteps"
            :key="stepNum"
            class="step"
            :class="{
              'completed': stepNum < currentStep,
              'active': stepNum === currentStep
            }"
          >
            <span class="step-number">{{ stepNum }}</span>
            <span class="step-title">{{ getStepTitle(stepNum) }}</span>
          </div>
        </div>
      </div>

      <!-- Step Content -->
      <div class="wizard-content">
        <!-- Step 1: Basic Information -->
        <div v-if="currentStep === 1" class="wizard-step">
          <h3 class="step-heading cabin-family semi-bold">Tell us about your finances</h3>

          <form @submit.prevent="nextStep">
            <div class="form-grid">
              <div class="form-group">
                <label for="name" class="form-label cabin-family semi-bold">Budget Name *</label>
                <input
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.name }"
                  placeholder="e.g., My Monthly Budget"
                />
                <div v-if="errors.name" class="error-message">{{ errors.name }}</div>
              </div>

              <div class="form-group">
                <label for="monthlyIncome" class="form-label cabin-family semi-bold">Monthly Income *</label>
                <CurrencyInput
                  v-model="form.monthlyIncome"
                  :has-error="!!errors.monthlyIncome"
                  :error-message="errors.monthlyIncome"
                  placeholder="Enter your monthly income"
                />
              </div>

              <div class="form-group">
                <label for="budgetPeriod" class="form-label cabin-family semi-bold">Budget Period</label>
                <select v-model="form.budgetPeriod" class="form-control">
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            </div>

            <div class="current-spending">
              <h4 class="cabin-family semi-bold">Current Monthly Spending (Optional)</h4>
              <p class="secondary-text">This helps us create a better budget for you</p>

              <div class="spending-grid">
                <div
                  v-for="category in defaultCategories"
                  :key="category.key"
                  class="form-group"
                >
                  <label :for="category.key" class="form-label">{{ category.name }}</label>
                  <CurrencyInput
                    v-model="form.spendingData[category.key]"
                    :placeholder="`Monthly ${category.name.toLowerCase()} spending`"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 2: Template Selection -->
        <div v-if="currentStep === 2" class="wizard-step">
          <div class="step-header">
            <button type="button" class="btn btn-link back-button" @click="previousStep">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <h3 class="step-heading cabin-family semi-bold">Choose a budget approach</h3>
          </div>

          <div class="template-options">
            <div
              class="template-card ai-template"
              :class="{ 'selected': form.selectedTemplate === 'ai', 'loading': loadingAI }"
              @click="selectTemplate('ai')"
            >
              <div class="template-header">
                <i class="fas fa-robot"></i>
                <h4 class="cabin-family semi-bold">AI Generated Budget</h4>
                <LoadingSpinner v-if="loadingAI" size="small" />
              </div>
              <p>Personalized budget based on your income and spending patterns</p>
              <div v-if="aiGenerated && form.selectedTemplate === 'ai'" class="template-preview">
                <div
                  v-for="(data, category) in aiGenerated.categories"
                  :key="category"
                  class="category-preview"
                >
                  <span>{{ formatCategoryName(category) }}</span>
                  <span>{{ data.percentage }}%</span>
                </div>
              </div>
            </div>

            <div
              class="template-card"
              :class="{ 'selected': form.selectedTemplate === 'manual' }"
              @click="selectTemplate('manual')"
            >
              <div class="template-header">
                <i class="fas fa-edit"></i>
                <h4 class="cabin-family semi-bold">Manual Budget</h4>
              </div>
              <p>Create your budget from scratch with full control</p>
              <div v-if="form.selectedTemplate === 'manual'" class="template-preview">
                <div class="category-preview">
                  <span>Housing</span>
                  <span>30%</span>
                </div>
                <div class="category-preview">
                  <span>Food</span>
                  <span>15%</span>
                </div>
                <div class="category-preview">
                  <span>Transportation</span>
                  <span>15%</span>
                </div>
                <div class="category-preview">
                  <span>Other</span>
                  <span>40%</span>
                </div>
              </div>
            </div>

            <div
              v-for="template in budgetTemplates"
              :key="template.id"
              class="template-card"
              :class="{ 'selected': form.selectedTemplate === template.id }"
              @click="selectTemplate(template.id)"
            >
              <div class="template-header">
                <i class="fas fa-chart-pie"></i>
                <h4 class="cabin-family semi-bold">{{ template.name }}</h4>
              </div>
              <p>{{ template.description }}</p>
              <div v-if="form.selectedTemplate === template.id" class="template-preview">
                <div
                  v-for="(data, category) in Object.entries(template.categories).slice(0, 4)"
                  :key="category"
                  class="category-preview"
                >
                  <span>{{ formatCategoryName(data[0]) }}</span>
                  <span>{{ data[1].percentage }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Customization -->
        <div v-if="currentStep === 3" class="wizard-step">
          <div class="step-header">
            <button type="button" class="btn btn-link back-button" @click="previousStep">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <h3 class="step-heading cabin-family semi-bold">Customize your budget</h3>
          </div>

          <div class="budget-customization">
            <div class="budget-overview">
              <div class="total-income">
                <span class="label">Monthly Income:</span>
                <span class="value primary cabin-family semi-bold">{{ formatCurrency(form.monthlyIncome) }}</span>
              </div>
            </div>

            <div class="categories-editor">
              <div
                v-for="(data, category) in currentBudgetCategories"
                :key="category"
                class="category-editor"
              >
                <div class="category-info">
                  <h5 class="cabin-family semi-bold">{{ formatCategoryName(category) }}</h5>
                  <p class="secondary-text">{{ data.description || 'Budget allocation for this category' }}</p>
                </div>

                <div class="category-controls">
                  <div class="percentage-input">
                    <input
                      v-model.number="form.customCategories[category].percentage"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      class="form-control"
                      @input="updateCategoryAmount(category)"
                    />
                    <span class="input-suffix">%</span>
                  </div>

                  <div class="amount-display">
                    <CurrencyInput
                      v-model="form.customCategories[category].amount"
                      @input="updateCategoryPercentage(category)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="budget-summary-final">
              <div class="summary-item">
                <span class="label">Total Allocated:</span>
                <span class="value">{{ totalAllocatedPercentage.toFixed(1) }}%</span>
              </div>
              <div class="summary-item">
                <span class="label">Total Amount:</span>
                <span class="value">{{ formatCurrency(totalAllocatedAmount) }}</span>
              </div>
              <div v-if="totalAllocatedPercentage !== 100" class="summary-warning">
                <i class="fas fa-exclamation-triangle"></i>
                Budget should total 100% (currently {{ totalAllocatedPercentage.toFixed(1) }}%)
              </div>
            </div>

            <div class="budget-options">
              <div class="form-check">
                <input
                  v-model="form.autoTrack"
                  type="checkbox"
                  class="form-check-input"
                  id="autoTrack"
                />
                <label for="autoTrack" class="form-check-label">
                  Enable automatic spending tracking
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Wizard Actions -->
      <div class="wizard-actions">
        <button
          v-if="currentStep > 1"
          type="button"
          class="btn btn-outline-secondary"
          @click="previousStep"
        >
          Back
        </button>

        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="closeWizard"
        >
          Cancel
        </button>

        <button
          v-if="currentStep < totalSteps"
          type="button"
          class="btn primary-btn"
          :disabled="!canProceedToNext() || loadingAI"
          @click="nextStep"
        >
          {{ loadingAI ? 'Loading...' : 'Next' }}
        </button>

        <button
          v-else
          type="button"
          class="btn primary-btn"
          :disabled="loading || totalAllocatedPercentage < 95 || totalAllocatedPercentage > 105"
          @click="submitBudget"
        >
          <LoadingSpinner v-if="loading" size="small" variant="white" />
          <span v-else>Create Budget</span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatCurrency } from '../../utils/formatters';
import { CurrencyInput, LoadingSpinner } from '../shared';

export default {
  name: 'BudgetCreationWizard',
  components: {
    CurrencyInput,
    LoadingSpinner,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      currentStep: 1,
      totalSteps: 3,
      loading: false,
      loadingAI: false,
      aiGenerated: null,
      form: {
        name: 'My Monthly Budget',
        monthlyIncome: '',
        budgetPeriod: 'monthly',
        spendingData: {},
        selectedTemplate: null,
        customCategories: {},
        autoTrack: false,
      },
      errors: {},
      defaultCategories: [
        { key: 'housing', name: 'Housing' },
        { key: 'food', name: 'Food' },
        { key: 'transportation', name: 'Transportation' },
        { key: 'utilities', name: 'Utilities' },
        { key: 'entertainment', name: 'Entertainment' },
        { key: 'shopping', name: 'Shopping' },
        { key: 'healthcare', name: 'Healthcare' },
        { key: 'savings', name: 'Savings' },
      ],
    };
  },
  computed: {
    ...mapState('budgets', ['budgetTemplates']),

    currentBudgetCategories() {
      if (this.form.selectedTemplate === 'ai' && this.aiGenerated) {
        return this.aiGenerated.categories;
      } if (this.form.selectedTemplate === 'manual') {
        // Default manual categories
        const manualCategories = {};
        this.defaultCategories.forEach((cat) => {
          manualCategories[cat.key] = {
            percentage: 0,
            description: `Budget allocation for ${cat.name.toLowerCase()}`,
          };
        });
        return manualCategories;
      } if (this.form.selectedTemplate && this.budgetTemplates.length > 0) {
        const template = this.budgetTemplates.find((t) => t.id === this.form.selectedTemplate);
        return template ? template.categories : {};
      }
      return {};
    },

    totalAllocatedPercentage() {
      return Object.values(this.form.customCategories)
        .reduce((total, cat) => total + (cat.percentage || 0), 0);
    },

    totalAllocatedAmount() {
      return Object.values(this.form.customCategories)
        .reduce((total, cat) => total + (cat.amount || 0), 0);
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        console.log('BudgetCreationWizard: visible prop changed to:', newVal);
        this.showModal = newVal;
        console.log('BudgetCreationWizard: showModal updated to:', this.showModal);
      },
    },
    showModal(newVal) {
      this.$emit('update:visible', newVal);
    },
    currentBudgetCategories: {
      immediate: true,
      handler(categories) {
        // Initialize custom categories when template changes
        Object.entries(categories).forEach(([categoryName, categoryData]) => {
          if (!this.form.customCategories[categoryName]) {
            this.$set(this.form.customCategories, categoryName, {
              percentage: categoryData.percentage || 0,
              amount: this.form.monthlyIncome
                ? (this.form.monthlyIncome * (categoryData.percentage || 0)) / 100
                : 0,
            });
          }
        });
      },
    },
  },
  methods: {
    ...mapActions('budgets', [
      'fetchBudgetTemplates',
      'generateAIBudget',
      'createBudget',
    ]),

    onModalShown() {
      console.log('BudgetCreationWizard: Modal is now shown!');
    },

    formatCurrency,

    getStepTitle(stepNum) {
      const titles = {
        1: 'Basic Info',
        2: 'Template',
        3: 'Customize',
      };
      return titles[stepNum] || '';
    },

    formatCategoryName(name) {
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },

    canProceedToNext() {
      switch (this.currentStep) {
        case 1:
          return this.form.name && this.form.monthlyIncome > 0;
        case 2:
          return this.form.selectedTemplate !== null;
        default:
          return true;
      }
    },

    async selectTemplate(templateId) {
      this.form.selectedTemplate = templateId;

      if (templateId === 'ai' && !this.aiGenerated) {
        await this.generateAI();
      }
    },

    async generateAI() {
      this.loadingAI = true;

      try {
        const result = await this.generateAIBudget({
          monthlyIncome: this.form.monthlyIncome,
          spendingData: this.form.spendingData,
        });

        if (result.success) {
          this.aiGenerated = result.data.template;
        } else {
          this.$toast.error('Failed to generate AI budget');
        }
      } catch (error) {
        console.error('AI generation error:', error);
        this.$toast.error('Failed to generate AI budget');
      } finally {
        this.loadingAI = false;
      }
    },

    updateCategoryAmount(category) {
      const percentage = this.form.customCategories[category].percentage || 0;
      const amount = (this.form.monthlyIncome * percentage) / 100;
      this.$set(this.form.customCategories[category], 'amount', amount);
    },

    updateCategoryPercentage(category) {
      const amount = this.form.customCategories[category].amount || 0;
      const percentage = this.form.monthlyIncome > 0
        ? (amount / this.form.monthlyIncome) * 100
        : 0;
      this.$set(this.form.customCategories[category], 'percentage', percentage);
    },

    validateStep1() {
      this.errors = {};

      if (!this.form.name.trim()) {
        this.$set(this.errors, 'name', 'Budget name is required');
      }

      if (!this.form.monthlyIncome || this.form.monthlyIncome <= 0) {
        this.$set(this.errors, 'monthlyIncome', 'Monthly income must be greater than 0');
      }

      return Object.keys(this.errors).length === 0;
    },

    async nextStep() {
      if (this.currentStep === 1 && !this.validateStep1()) return;

      if (this.currentStep === 1) {
        // Load templates when moving to step 2
        try {
          await this.fetchBudgetTemplates(this.form.monthlyIncome);
        } catch (error) {
          console.error('Error loading templates:', error);
        }
      }

      this.currentStep += 1;
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },

    async submitBudget() {
      this.loading = true;

      try {
        const budgetData = {
          name: this.form.name,
          monthlyIncome: this.form.monthlyIncome,
          budgetPeriod: this.form.budgetPeriod,
          categories: this.form.customCategories,
          autoTrack: this.form.autoTrack,
        };

        const result = await this.createBudget(budgetData);

        if (result.success) {
          this.$toast.success('Budget created successfully!');
          this.$emit('budget-created', result.data.budget);
          this.closeWizard();
        } else {
          this.$toast.error(result.error || 'Failed to create budget');
        }
      } catch (error) {
        console.error('Budget creation error:', error);
        this.$toast.error('An error occurred while creating the budget');
      } finally {
        this.loading = false;
      }
    },

    resetWizard() {
      this.currentStep = 1;
      this.form = {
        name: 'My Monthly Budget',
        monthlyIncome: '',
        budgetPeriod: 'monthly',
        spendingData: {},
        selectedTemplate: null,
        customCategories: {},
        autoTrack: false,
      };
      this.errors = {};
      this.loading = false;
      this.loadingAI = false;
      this.aiGenerated = null;
    },

    closeWizard() {
      this.showModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.budget-wizard {
  .wizard-progress {
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #E0E0E0;
  }

  .progress-steps {
    display: flex;
    justify-content: space-between;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 15px;
      left: 15px;
      right: 15px;
      height: 2px;
      background: #E0E0E0;
      z-index: 1;
    }
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;

    &.completed .step-number {
      background: $success;
      color: white;
    }

    &.active .step-number {
      background: $primary;
      color: white;
    }
  }

  .step-number {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #E0E0E0;
    color: $secondary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 0.5rem;
  }

  .step-title {
    font-size: 12px;
    color: $secondary;
    text-align: center;
  }

  .wizard-content {
    min-height: 400px;
    margin-bottom: 2rem;
  }

  .step-heading {
    color: $black;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  .step-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;

    .back-button {
      color: $primary;
      text-decoration: none;
      margin-right: 1rem;

      &:hover {
        color: darken($primary, 10%);
      }
    }

    .step-heading {
      margin-bottom: 0;
      text-align: left;
    }
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    color: $black;
  }

  .error-message {
    color: $primary;
    font-size: 12px;
    margin-top: 0.25rem;
  }

  .current-spending {
    h4 {
      color: $black;
      margin-bottom: 0.5rem;
    }

    p {
      margin-bottom: 1rem;
    }
  }

  .spending-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .template-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
  }

  .template-card {
    background: $white;
    border: 2px solid #E0E0E0;
    border-radius: 12px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $primary;
      transform: translateY(-2px);
    }

    &.selected {
      border-color: $primary;
      background: $primary-light;
    }

    &.loading {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .template-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    i {
      font-size: 24px;
      color: $primary;
    }

    h4 {
      margin-bottom: 0;
      color: $black;
    }
  }

  .template-preview {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #E0E0E0;
  }

  .category-preview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .budget-customization {
    .budget-overview {
      background: #F8F9FA;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 2rem;
      text-align: center;

      .total-income {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;

        .label {
          font-size: 16px;
          color: $secondary;
        }

        .value {
          font-size: 20px;
        }
      }
    }
  }

  .categories-editor {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .category-editor {
    background: #F8F9FA;
    border-radius: 8px;
    padding: 1rem;
  }

  .category-info {
    margin-bottom: 1rem;

    h5 {
      margin-bottom: 0.25rem;
      color: $black;
    }

    p {
      margin-bottom: 0;
      font-size: 12px;
    }
  }

  .category-controls {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .percentage-input {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 120px;

    input {
      width: 80px;
    }

    .input-suffix {
      font-weight: 600;
      color: $secondary;
    }
  }

  .amount-display {
    flex: 1;
  }

  .budget-summary-final {
    background: $primary-light;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;

    .summary-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }

      .label {
        font-weight: 600;
        color: $black;
      }

      .value {
        font-weight: 600;
        color: $primary;
      }
    }

    .summary-warning {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.75rem;
      padding: 0.5rem;
      background: rgba($primary, 0.1);
      border-radius: 4px;
      font-size: 12px;
      color: $primary;
    }
  }

  .budget-options {
    .form-check {
      margin-bottom: 0;
    }
  }

  .wizard-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #E0E0E0;

    .btn {
      min-width: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
  }
}

@media (max-width: 768px) {
  .budget-wizard {
    .template-options {
      grid-template-columns: 1fr;
    }

    .form-grid, .spending-grid {
      grid-template-columns: 1fr;
    }

    .category-controls {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
    }

    .percentage-input {
      flex: none;
      justify-content: space-between;
    }

    .wizard-actions {
      flex-direction: column;

      .btn {
        width: 100%;
      }
    }
  }
}
</style>
